import { useState } from "react";
import { toast } from "sonner";
import { Gift, Percent, Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { FormDialog, PageHead, Stat, money, useApi } from "@/components/shop/common";

const STATUS = { trialing: "Trial", active: "Active", past_due: "Past due", canceled: "Canceled", unpaid: "Unpaid", incomplete: "Incomplete" };
const date = (d) => (d ? new Date(d).toLocaleDateString() : "");

const statusText = (s) => {
  if (s.free_access) return `Free until ${date(s.free_until)}`;
  if (s.status === "trialing") return `Trial until ${date(s.trial_end)}`;
  return STATUS[s.status] || "No plan";
};

const DISCOUNT_FIELDS = [
  { name: "percent_off", label: "Percent off", type: "number", required: true, half: true },
  { name: "duration", label: "How long", type: "select", half: true, options: [{ value: "repeating", label: "Several months" }, { value: "once", label: "First payment only" }, { value: "forever", label: "Forever" }] },
  { name: "months", label: "Months (if several)", type: "number" },
];
const PLAN_FIELDS = [
  { name: "name", label: "Plan name", required: true },
  { name: "amount", label: "Monthly price (USD)", type: "number", required: true, half: true },
  { name: "staff_limit", label: "Staff logins (0 = unlimited)", type: "number", half: true },
  { name: "description", label: "Description", type: "textarea" },
];

export default function Admin() {
  const [d, load] = useApi("/admin/shops");
  const [plans, loadPlans] = useApi("/admin/plans");
  const [free, setFree] = useState(null);
  const [disc, setDisc] = useState(null);
  const [plan, setPlan] = useState(null);
  const [q, setQ] = useState("");
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const shops = (d?.shops || []).filter((s) => `${s.owner_email} ${s.shop_name} ${s.owner_name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <PageHead eyebrow="Platform" title="Admin panel" />
      {d && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Stat label="Shops" value={d.stats.total} testid="admin-stat-total" />
          <Stat label="Paying" value={d.stats.active} accent="text-emerald-400" testid="admin-stat-active" />
          <Stat label="On trial" value={d.stats.trialing} testid="admin-stat-trialing" />
          <Stat label="Free access" value={d.stats.free} accent="text-amber-400" testid="admin-stat-free" />
          <Stat label="MRR" value={money(d.stats.mrr)} accent="text-primary" testid="admin-stat-mrr" />
        </div>
      )}
      <h2 className="font-display text-2xl uppercase mb-3">Plans</h2>
      <div className="grid sm:grid-cols-2 gap-4 mb-10 max-w-3xl">
        {plans?.map((p) => (
          <div key={p.key} className="bg-card border border-border rounded-sm p-5 flex justify-between gap-3" data-testid={`admin-plan-${p.key}`}>
            <div><p className="font-display text-2xl uppercase">{p.name}</p><p className="font-mono text-sm">{money(p.amount)}/mo · {p.staff_limit ? `${p.staff_limit} staff` : "unlimited staff"}</p></div>
            <Button variant="ghost" size="icon" onClick={() => setPlan(p)} data-testid={`admin-plan-edit-${p.key}`}><Pencil className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap justify-between items-end gap-3 mb-3">
        <h2 className="font-display text-2xl uppercase">Shops</h2>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search shops…" className="h-10 px-3 rounded-sm bg-card border border-input text-sm w-full sm:w-72" data-testid="admin-search-input" />
      </div>
      <div className="border border-border rounded-sm bg-card/80 overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-shops-table">
          <thead className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            <tr className="border-b border-border">{["Shop / owner", "Plan", "Status", "Discount", "Staff", "Jobs", ""].map((h) => <th key={h} className="text-left p-3 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody>
            {shops.map((s) => {
              const sub = s.subscription;
              return (
                <tr key={s.shop_id} className="border-b border-border last:border-0" data-testid={`admin-shop-row-${s.shop_id}`}>
                  <td className="p-3"><p className="font-semibold">{s.shop_name || s.owner_name || "—"}</p><p className="text-xs font-mono text-muted-foreground">{s.owner_email}{s.role === "admin" && " · admin"}</p></td>
                  <td className="p-3 capitalize">{sub.plan_key || "—"}</td>
                  <td className="p-3" data-testid={`admin-shop-status-${s.shop_id}`}>{statusText(sub)}{sub.cancel_at_period_end && " (canceling)"}</td>
                  <td className="p-3" data-testid={`admin-shop-discount-${s.shop_id}`}>{sub.discount ? (
                    <span className="inline-flex items-center gap-1 text-emerald-400">{sub.discount.percent_off}% · {sub.discount.duration === "repeating" ? `${sub.discount.months} mo` : sub.discount.duration}
                      <button onClick={() => act(() => api.delete(`/admin/shops/${s.shop_id}/discount`), "Discount removed")} className="text-muted-foreground hover:text-red-400" data-testid={`admin-discount-remove-${s.shop_id}`}><X className="h-3.5 w-3.5" /></button></span>) : "—"}</td>
                  <td className="p-3 font-mono">{s.staff_count}</td>
                  <td className="p-3 font-mono">{s.work_orders}</td>
                  <td className="p-3 whitespace-nowrap">
                    <Button size="sm" variant="outline" className="rounded-sm mr-2 hover:text-white" onClick={() => setFree(s)} data-testid={`admin-free-btn-${s.shop_id}`}><Gift className="h-3.5 w-3.5 mr-1" /> Free time</Button>
                    <Button size="sm" variant="outline" className="rounded-sm mr-2 hover:text-white" onClick={() => setDisc(s)} data-testid={`admin-discount-btn-${s.shop_id}`}><Percent className="h-3.5 w-3.5 mr-1" /> Discount</Button>
                    {sub.free_access && <Button size="sm" variant="outline" className="rounded-sm hover:text-red-400" onClick={() => act(() => api.delete(`/admin/shops/${s.shop_id}/free-access`), "Free access removed")} data-testid={`admin-free-revoke-${s.shop_id}`}>End free</Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <FormDialog open={!!free} onOpenChange={(o) => !o && setFree(null)} title={`Free time · ${free?.owner_email}`} testid="admin-free-dialog" initial={{ days: 30 }}
        fields={[{ name: "days", label: "Days of free access (extends a running trial)", type: "number", required: true }]}
        onSubmit={async (v) => { const { data } = await api.post(`/admin/shops/${free.shop_id}/free-access`, { days: Math.round(v.days) }); toast.success(data.extended === "trial" ? "Trial extended" : "Free access granted"); load(); }} />
      <FormDialog open={!!disc} onOpenChange={(o) => !o && setDisc(null)} title={`Discount · ${disc?.owner_email}`} testid="admin-discount-dialog" initial={{ percent_off: 20, duration: "repeating", months: 3 }} fields={DISCOUNT_FIELDS}
        onSubmit={async (v) => { const { data } = await api.post(`/admin/shops/${disc.shop_id}/discount`, { ...v, months: Math.round(v.months || 1) }); toast.success(data.applied_to_subscription ? "Discount applied to their subscription" : "Discount saved — applies when they subscribe"); load(); }} />
      <FormDialog open={!!plan} onOpenChange={(o) => !o && setPlan(null)} title="Edit plan" testid="admin-plan-dialog" initial={plan} fields={PLAN_FIELDS}
        onSubmit={async (v) => { await api.put(`/admin/plans/${plan.key}`, { ...v, staff_limit: Math.round(v.staff_limit), active: true }); toast.success("Plan updated — new signups get the new price"); loadPlans(); }} />
    </>
  );
}
